import { FaArrowLeft, FaSyncAlt } from "react-icons/fa";
import { Link, useParams } from "react-router-dom";
import PageLoading from "src/components/pageLoading";
import { useTheme } from "src/context/theme.context";
import { displayDate } from "src/utils/dateHandler.util";
import { useGetTaskFailureByIdQuery } from "../../task-failures.service";
import { TaskFailure } from "../../types/task-failure.entity";

const DetailItem = ({ label, value, isDarkMode }: { label: string; value: string | number; isDarkMode: boolean }) => (
  <div className={`border-l-2 px-3 py-1 ${isDarkMode ? "border-sky-400/50" : "border-sky-500"}`}>
    <span className={`block text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{label}</span>
    <span className={`block break-all text-sm font-medium ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>{value}</span>
  </div>
);

const RelatedEntities = ({ taskFailure, isDarkMode }: { taskFailure: TaskFailure; isDarkMode: boolean }) => {
  const entries = Object.entries(taskFailure.relatedEntities).filter(
    ([type, value]) => type !== "userId" || value !== taskFailure.userId
  );

  if (!taskFailure.userId && !entries.length) return null;

  return (
    <section className="mt-8">
      <h2 className={`mb-3 text-base font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Related records</h2>
      <div className="flex flex-wrap gap-2">
        {taskFailure.userId && (
          <Link
            to={`/users/${taskFailure.userId}`}
            className={`border px-3 py-2 no-underline transition-colors ${isDarkMode ? "border-sky-400/30 bg-sky-500/10 text-sky-200 hover:bg-sky-500/20" : "border-sky-200 bg-sky-50 text-sky-800 hover:bg-sky-100"}`}
          >
            <span className="mr-2 text-xs font-medium">User</span>
            <span className="font-mono text-xs">{taskFailure.userId}</span>
          </Link>
        )}
        {entries.map(([type, id]) => (
          <span key={`${type}:${id}`} className={`border px-3 py-2 ${isDarkMode ? "border-slate-600 bg-slate-800 text-slate-200" : "border-slate-200 bg-slate-50 text-slate-700"}`}>
            <span className="mr-2 text-xs font-medium">{type}</span>
            <span className="font-mono text-xs">{id}</span>
          </span>
        ))}
      </div>
    </section>
  );
};

const TaskFailureDetailsPage = () => {
  const { id } = useParams();
  const { isDarkMode } = useTheme();
  const { data: taskFailure, isLoading, isFetching, isError, refetch } = useGetTaskFailureByIdQuery(id ?? "", {
    skip: !id,
  });

  if (isLoading) return <PageLoading />;

  if (isError) {
    return (
      <div className="page_container">
        <div className={`border p-4 text-sm ${isDarkMode ? "border-rose-500/30 bg-rose-500/10 text-rose-200" : "border-rose-200 bg-rose-50 text-rose-700"}`}>
          Could not load this task failure. Please try again.
        </div>
      </div>
    );
  }

  if (!taskFailure) {
    return (
      <div className="page_container">
        <Link to="/task-failures" className={`inline-flex items-center gap-2 text-sm no-underline ${isDarkMode ? "text-sky-300" : "text-sky-700"}`}>
          <FaArrowLeft />
          Task Failures
        </Link>
        <div className={`mt-6 border px-6 py-14 text-center ${isDarkMode ? "border-slate-700 bg-slate-900/50 text-slate-300" : "border-slate-200 bg-slate-50 text-slate-600"}`}>
          This task failure is no longer current.
        </div>
      </div>
    );
  }

  const isBusy = isFetching;

  return (
    <div className="page_container">
      <div className="mb-6 flex flex-col gap-4 lg:flex-row lg:items-end lg:justify-between">
        <div>
          <Link to="/task-failures" className={`mb-3 inline-flex items-center gap-2 text-sm no-underline ${isDarkMode ? "text-sky-300 hover:text-sky-200" : "text-sky-700 hover:text-sky-900"}`}>
            <FaArrowLeft />
            Task Failures
          </Link>
          <h1 className={`text-2xl font-semibold ${isDarkMode ? "text-slate-50" : "text-slate-900"}`}>{taskFailure.functionName}</h1>
          <p className={`mt-1 font-mono text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{taskFailure.taskId}</p>
        </div>
        <button
          type="button"
          onClick={() => refetch()}
          disabled={isBusy}
          title="Refresh task failure"
          aria-label="Refresh task failure"
          className={`inline-flex h-10 w-10 items-center justify-center self-start rounded border transition-colors disabled:cursor-not-allowed disabled:opacity-50 lg:self-auto ${isDarkMode ? "border-slate-600 text-slate-200 hover:border-sky-400 hover:bg-sky-500/15" : "border-slate-200 text-slate-700 hover:border-sky-300 hover:bg-sky-50"}`}
        >
          <FaSyncAlt className={isBusy ? "animate-spin" : ""} />
        </button>
      </div>

      <section className={`border ${isDarkMode ? "border-slate-700 bg-slate-900/50" : "border-slate-200 bg-white"}`}>
        <div className="grid gap-5 p-5 sm:grid-cols-2 xl:grid-cols-4">
          <DetailItem label="Queue" value={taskFailure.queueName} isDarkMode={isDarkMode} />
          <DetailItem label="Failures" value={taskFailure.failureCount} isDarkMode={isDarkMode} />
          <DetailItem label="Retry count" value={taskFailure.retryCount} isDarkMode={isDarkMode} />
          <DetailItem label="Execution count" value={taskFailure.executionCount} isDarkMode={isDarkMode} />
          <DetailItem label="First failed" value={displayDate(taskFailure.firstFailedAt)} isDarkMode={isDarkMode} />
          <DetailItem label="Last failed" value={displayDate(taskFailure.lastFailedAt)} isDarkMode={isDarkMode} />
          {taskFailure.maxAttempts !== undefined && <DetailItem label="Maximum attempts" value={taskFailure.maxAttempts} isDarkMode={isDarkMode} />}
          {taskFailure.scheduledAt && <DetailItem label="Scheduled for" value={displayDate(taskFailure.scheduledAt)} isDarkMode={isDarkMode} />}
        </div>
      </section>

      <section className="mt-8">
        <h2 className={`mb-3 text-base font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Last error</h2>
        <div className={`border-l-4 p-4 ${isDarkMode ? "border-rose-400 bg-rose-500/10" : "border-rose-500 bg-rose-50"}`}>
          {taskFailure.lastError.code && <p className={`mb-2 font-mono text-xs ${isDarkMode ? "text-rose-200" : "text-rose-700"}`}>{taskFailure.lastError.code}</p>}
          <p className={`whitespace-pre-wrap text-sm ${isDarkMode ? "text-rose-100" : "text-rose-900"}`}>{taskFailure.lastError.message}</p>
          {taskFailure.lastError.stackTop && <pre className={`mt-4 overflow-x-auto whitespace-pre-wrap border-t pt-4 text-xs ${isDarkMode ? "border-rose-400/20 text-rose-200" : "border-rose-200 text-rose-800"}`}>{taskFailure.lastError.stackTop}</pre>}
        </div>
      </section>

      <RelatedEntities taskFailure={taskFailure} isDarkMode={isDarkMode} />

      <section className="mt-8">
        <h2 className={`mb-3 text-base font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Failure attempts</h2>
        <div className={`overflow-x-auto border ${isDarkMode ? "border-slate-700" : "border-slate-200"}`}>
          <table className="min-w-[820px] w-full divide-y divide-slate-200 text-left text-sm dark:divide-slate-700">
            <thead className={isDarkMode ? "bg-slate-800 text-slate-200" : "bg-slate-900 text-white"}>
              <tr className="text-xs uppercase tracking-wide">
                <th className="px-4 py-3 font-semibold">Failed at</th>
                <th className="px-4 py-3 font-semibold">Retry / execution</th>
                <th className="px-4 py-3 font-semibold">Response</th>
                <th className="px-4 py-3 font-semibold">Error</th>
              </tr>
            </thead>
            <tbody className={isDarkMode ? "divide-y divide-slate-800" : "divide-y divide-slate-100"}>
              {taskFailure.attempts.map((attempt, index) => (
                <tr key={`${attempt.executionCount}:${attempt.retryCount}:${index}`} className={isDarkMode ? "bg-slate-900/50" : "bg-white"}>
                  <td className={`px-4 py-3 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{displayDate(attempt.failedAt)}</td>
                  <td className={`px-4 py-3 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{attempt.retryCount} / {attempt.executionCount}</td>
                  <td className={`px-4 py-3 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>{attempt.previousResponse ?? "-"}</td>
                  <td className={`max-w-[500px] px-4 py-3 ${isDarkMode ? "text-slate-200" : "text-slate-700"}`}>
                    <p className="truncate" title={attempt.error.message}>{attempt.error.message}</p>
                    {attempt.retryReason && <p className={`mt-1 text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>{attempt.retryReason}</p>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="mt-8">
        <h2 className={`mb-3 text-base font-semibold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Payload</h2>
        <pre className={`max-h-[420px] overflow-auto border p-4 text-xs ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-200" : "border-slate-200 bg-slate-50 text-slate-800"}`}>{taskFailure.payload}</pre>
      </section>
    </div>
  );
};

export default TaskFailureDetailsPage;
